import PaginationControls from "@/components/tables/pagination/PaginationControls";
import PaginationInfo from "@/components/tables/pagination/PaginationInfo";
import TableComponent from "@/components/tables/table/TableComponent";
import {
  Box,
  Button,
  Group,
  Image,
  Loader,
  Modal,
  Popover,
  rem,
  Space,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import ImgArrow from "@/assets/images/pngs/img-arrow.png";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useLazyGetTransHistoryQuery } from "@/services/transactionAPI";
import { get } from "lodash";
import { useViewAccountStatementQuery } from "@/services/dashboardAPI";
import { toCurrency } from "@/utils/common";
import RoutesString from "@/pages/routesString";
import { useNavigate } from "react-router-dom";
import IconCarlender from "@/assets/images/pngs/calendar-02.png";
import ImgNotiFail from "@/assets/images/pngs/noti-fail.png";

const PAGE_SIZE = 10;

const ViewStatement = ({
  sourceLinkId,
  accountNo,
  onDateRangeChange,
}: {
  sourceLinkId: string;
  accountNo: string;
  onDateRangeChange?: (from: string, to: string) => void;
}) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [dateError, setDateError] = useState<string>("");
  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false);
  /** fromDate/toDate mặc định 30 ngày gần nhất */
  const defaultFrom = dayjs().subtract(30, "day").format("YYYYMMDD");
  const defaultTo = dayjs().format("YYYYMMDD");
  const [fromDate, setFromDate] = useState<string>(defaultFrom);
  const [toDate, setToDate] = useState<string>(defaultTo);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const theme = useMantineTheme();

  const [draftRange, setDraftRange] = useState<[Date | null, Date | null]>([
    dayjs().subtract(30, "day").toDate(),
    dayjs().toDate(),
  ]);

  const openPicker = () => setOpened(true);
  const closePicker = () => setOpened(false);

  const [triggerTrans, { data: dataTrans, isFetching }] =
    useLazyGetTransHistoryQuery();

  useEffect(() => {
    onDateRangeChange?.(fromDate, toDate);
  }, [fromDate, toDate]);

  const statementItems: any[] = get(dataTrans, "body.fundsTransfer.items", []);
  const totalPages: number = get(dataTrans, "body.fundsTransfer.totalPages", 1);

  useEffect(() => {
    if (!sourceLinkId) return;
    triggerTrans({
      keyword: "",
      sourceLinkId,
      fromDate,
      toDate,
      minAmount: "",
      maxAmount: "",
      transType: "",
      amountType: "",
      pageNo: page,
      pageSize: PAGE_SIZE,
      type: "STATEMENT",
      transChannel: "",
    });
  }, [page, sourceLinkId, fromDate, toDate]);

  useEffect(() => {
    setPage(1);
  }, [sourceLinkId]);

  const apply = () => {
    const [from, to] = draftRange;
    if (!from || !to) {
      setDateError(t("DATE_RANGE_REQUIRED") as string);
      return;
    }
    if (dayjs().diff(dayjs(from), "day") > 365) {
      setDateError(t("DATE_RANGE_MAX_365_DAYS") as string);
      return;
    }
    if (dayjs(to).diff(dayjs(from), "day") > 90) {
      setOpened(false);
      setDateRangeModalOpen(true);
      return;
    }
    setDateError("");
    setFromDate(dayjs(from).format("YYYYMMDD"));
    setToDate(dayjs(to).format("YYYYMMDD"));
    setPage(1);
    setOpened(false);
  };

  const { data: dataStatement } = useViewAccountStatementQuery({
    accountNo,
  });

  const displayDate = (raw: string): string => {
    if (!raw) return "--/--/----";
    const d = dayjs(raw, "YYYYMMDD");
    return d.isValid() ? d.format("DD/MM/YYYY") : "--/--/----";
  };

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid #D8DDE2",
        }}
      >
        {/* Left */}
        <Group spacing={48} noWrap>
          <Box>
            <Text size="sm" c="dimmed">
              {t("STATEMENT_OPENING_BALANCE")}
            </Text>
            <Text fw={800} size="md" style={{ color: "#111827" }} w={150}>
              {toCurrency(dataStatement?.body?.openingBalance || "0")} VND
            </Text>
          </Box>
          <Image src={ImgArrow} alt="arrow" />
          <Box>
            <Text size="sm" c="dimmed">
              {t("STATEMENT_CLOSING_BALANCE")}
            </Text>
            <Text fw={800} size="md" style={{ color: "#111827" }} w={200}>
              {toCurrency(dataStatement?.body?.closingBalance || "0")} VND
            </Text>
          </Box>
        </Group>

        {/* Right */}
        <Group spacing={10} noWrap>
          <Group
            spacing={48}
            noWrap
            style={{
              border: "1px solid #E1E4EA",
              padding: "8px 10px",
              borderRadius: "10px",
            }}
          >
            <Popover
              opened={opened}
              onChange={setOpened}
              position="bottom-start"
              withArrow
              shadow="md"
              withinPortal
            >
              <Popover.Target>
                <Button
                  variant="subtle"
                  onClick={openPicker}
                  styles={{
                    root: {
                      padding: 0,
                      height: "auto",
                      background: "transparent",
                    },
                  }}
                >
                  <Group spacing={8} noWrap>
                    <Image src={IconCarlender} alt="calendar" width={16} />
                    <Text size="sm" fw={600} style={{ color: "#0171BB" }}>
                      {t("QUERY_PERIOD")}
                    </Text>
                  </Group>
                </Button>
              </Popover.Target>

              <Popover.Dropdown>
                <Stack spacing={12}>
                  <DatePicker
                    type="range"
                    value={draftRange}
                    onChange={(val) => { setDraftRange(val); setDateError(""); }}
                    minDate={dayjs().subtract(365, "day").toDate()}
                    maxDate={dayjs().toDate()}
                    allowSingleDateInRange
                  />
                  {dateError && (
                    <Text size="xs" c="red">{dateError}</Text>
                  )}
                  <Group position="right" mt="sm">
                    <Button variant="default" size="sm" onClick={closePicker}>
                      {t("CANCEL_SHORT")}
                    </Button>
                    <Button size="sm" onClick={apply}>
                      {t("APPLY")}
                    </Button>
                  </Group>
                </Stack>
              </Popover.Dropdown>
            </Popover>

            <Text size="sm" style={{ color: "#111827" }}>
              {displayDate(fromDate)}
            </Text>

            <Box style={{ color: "#0171BB", display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 0 }}>
              <IconArrowNarrowLeft size={18} style={{ marginBottom: -8 }} />
              <IconArrowNarrowRight size={18} />
            </Box>

            <Text size="sm" style={{ color: "#111827" }}>
              {displayDate(toDate)}
            </Text>
          </Group>
        </Group>
      </Box>

      <TableComponent
        dataSource={{
          columns: [
            { title: `${t("REFERENCE_CODE")}` },
            { title: t("TRANSACTION_TIME") },
            { title: `${t("CONTENT_BATCH")}`, size: 200 },
            { title: t("DEBIT_AMOUNT_COL") },
            { title: t("CREDIT_AMOUNT_COL") },
            { title: t("REMAINING_BALANCE") },
          ],
          data: statementItems.map((item: any) => {
            return {
              transactionId: (
                <Text
                  weight={600}
                  c="#0171BB"
                  style={{
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 160,
                  }}
                  title={item.transId}
                  onClick={() =>
                    navigate(
                      `${RoutesString.TransHistoryDetail}/${item.transId}`,
                      {
                        state: {
                          transId: item.transId,
                          accountNo,
                          type: "STATEMENT",
                        },
                      },
                    )
                  }
                >
                  {item.transId}
                </Text>
              ),
              transactionDate: (
                <Text>
                  {item.displayTime}{" "}
                  {item.displayDate}
                </Text>
              ),
              transactionRemarks: item.fromMessage,
              debitAmount: (
                <Text c="#F37021" fw={600} ta="left">
                  {item.debitAmount && item.debitAmount !== "-"
                    ? `${item.displayAmount}`
                    : "-"}
                </Text>
              ),
              creditAmount: (
                <Text c="#34B529" fw={600} ta="left">
                  {item.creditAmount && item.creditAmount !== "-"
                    ? `${item.displayAmount}`
                    : "-"}
                </Text>
              ),
              displayBalanceAfter: (
                <Text fw={600} ta="left">
                  {item.displayBalanceAfter}
                </Text>
              ),
            };
          }),
        }}
      />

      {isFetching && (
        <Group position="center" py={rem(12)}>
          <Loader size="sm" color={theme.colors.blue[6]} />
        </Group>
      )}
      {!isFetching && statementItems.length === 0 && (
        <Text ta="center" c="dimmed" py={rem(24)} size="sm">
          {t("NO_DATA")}
        </Text>
      )}
      <Space h={rem(10)} />
      <Group position="apart" sx={{ padding: "12px 20px" }}>
        <PaginationInfo current={page} total={totalPages} />
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </Group>

      <Modal
        opened={dateRangeModalOpen}
        onClose={() => setDateRangeModalOpen(false)}
        withCloseButton={false}
        centered
        size="sm"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "16px 8px 8px",
            textAlign: "center",
            gap: 12,
          }}
        >
          <img src={ImgNotiFail} alt="error" width={80} height={80} />
          <Text fw={600} fz={16}>
            {t("NOTICE")}
          </Text>
          <Text fz={14} color="dimmed">
            {t("QUERY_WITHIN_90_DAYS")}
          </Text>
          <Button
            fullWidth
            mt={4}
            sx={{
              backgroundColor: "#2A81D0",
              borderRadius: 8,
              "&:hover": { backgroundColor: "#1E679E" },
            }}
            onClick={() => setDateRangeModalOpen(false)}
          >
            {t("CLOSE")}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ViewStatement;
